// Control Center Dashboard (Blueprint §4.3.1 / D-3) — bound to bccGetDashboard.

import { useCallback, useEffect, useState } from "react";
import { SectionHeader, StatBlock } from "../../../components/control";
import { bccGetDashboardCallable, type BccDashboardData } from "../../../lib/bccPhase1Callables";

const AED = (n: number) => `AED ${Math.round(n).toLocaleString()}`;

export function ControlDashboardPage() {
  const [data, setData] = useState<BccDashboardData | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await bccGetDashboardCallable({});
      setData(res.data);
      setUpdatedAt(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const c = data?.cards;
  const f = data?.finance;
  const h = data?.health;
  const v = (n: number | undefined) => (n === undefined ? "—" : String(n));

  return (
    <div className="space-y-5">
      <SectionHeader title="Dashboard" subtitle="Live overview of the platform business." updatedAt={updatedAt} onRefresh={() => void load()} refreshing={loading} />
      {error ? <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">{error}</p> : null}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        <StatBlock label="Active tenants" value={v(c?.activeTenants)} />
        <StatBlock label="Demo tenants" value={v(c?.demoTenants)} />
        <StatBlock label="Pending proposals" value={v(c?.pendingProposals)} />
        <StatBlock label="Payment failed" value={v(c?.paymentFailed)} />
        <StatBlock label="Low credit" value={v(c?.lowCreditTenants)} />
        <StatBlock label="Open support" value={v(c?.openSupportIssues)} />
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatBlock label="Subscription revenue MTD" value={f ? AED(f.monthlySubscriptionRevenueAed) : "AED —"} />
        <StatBlock label="Credit pack revenue MTD" value={f ? AED(f.creditRevenueAed) : "AED —"} />
        <StatBlock label="Estimated AI cost MTD" value={f ? AED(f.estimatedAiCostAed) : "AED —"} />
        <StatBlock label="Net margin MTD" value={f ? `${AED(f.netMarginAed)} · ${f.netMarginPercent}%` : "—"} />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-sm font-black text-slate-900">System status</p>
        <p className="mt-2 text-sm text-slate-500">
          Reconciliation alerts: <span className="font-bold text-slate-900">{v(h?.reconciliationAlerts)}</span>
          {" · "}Webhooks: <span className={`font-bold ${h?.webhookLagging ? "text-orange-600" : "text-emerald-600"}`}>{h?.webhookLagging ? "lagging" : "ok"}</span>
        </p>
      </div>
    </div>
  );
}
